import React from "react";
import toast from "react-hot-toast";
import { oidcClient } from "../../../redux/token";

const MicroserviceManagementContext = React.createContext<{
    isSeedingAvailable: boolean
    isServiceAvailable: boolean
    executeSeed: () => Promise<void>
}>({ 
    isSeedingAvailable: false,
    isServiceAvailable: false,
    executeSeed: () => new Promise<void>((resolve) => resolve())
});

export const useMicroserviceManagement = () => React.useContext(MicroserviceManagementContext);

export const MicroserviceManagementProvider = ({
    children,
    healthEndpoint,
    healthInterval = 30000,
    seedEndpoint
} : Readonly<{
    children: React.ReactNode,
    healthEndpoint?: string | null,
    healthInterval?: number,
    seedEndpoint?: string | null
}>) => {
    const [isAvailable, setIsAvailable] = React.useState<boolean>(true);

    const executeHealthCheck = async () => {
        if (!healthEndpoint) {
            return;
        }

        let tokenData = oidcClient.getOidcToken();
        const healthCheckResult = await fetch(healthEndpoint, {
            method: 'GET',
            headers: {
                'Authorization': `${tokenData?.token_type} ${tokenData?.access_token}`,
                'Content-Type': 'application/json'
            },
        });

        setIsAvailable(() => healthCheckResult.status === 200);
    };

    const executeSeed = () : Promise<void> => new Promise((resolve) => {
        if (!seedEndpoint) {
            return;
        }
        let tokenData = oidcClient.getOidcToken();
        return fetch(seedEndpoint, {
            method: 'POST',
            headers: {
                'Authorization': `${tokenData?.token_type} ${tokenData?.access_token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                seedPageLimit: 5000,
                rpcTimeout: 300000
            })
        })
        .then(() => toast('Seeding process has started.'))
        .catch(() => toast.error('Failed to start seeding process.'))
        .finally(() => resolve());
    });

    React.useEffect(() => {
        // execute the first time because the interval will do the full wait first
        executeHealthCheck();

        // this sets up a polling interval to check the service is available or not
        let intervalId = setInterval(executeHealthCheck, healthInterval);
        return () => {
            clearInterval(intervalId);
        }
    }, [ healthEndpoint, healthInterval ]);

    return (
        <MicroserviceManagementContext.Provider value={{
            isSeedingAvailable: !seedEndpoint ? false : true,
            isServiceAvailable: isAvailable,
            executeSeed
        }}>
            {children}
        </MicroserviceManagementContext.Provider>
    );
};